import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@langchain/untitled-ui-icons';

import React, { ReactNode, useState } from 'react';
import { Link, useMatches } from 'react-router-dom';

import { CopyInlineLink } from '@/components/CopyInlineLink';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { Button, IconButton, Text, Tooltip } from '@/design-system';
import { useOrganizationId } from '@/hooks/useSwr';
import { useTextClamped } from '@/hooks/useTextClamped';
import { RunSchema } from '@/types/schema';
import {
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { isUrl } from '../utils/utils';

// We'll use CSS text clamping instead of manual truncation

interface FullContentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  path?: string;
  run?: RunSchema;
}

function FullContentDialog({
  isOpen,
  onClose,
  title,
  content,
  path,
  run,
}: FullContentDialogProps) {
  const organizationId = useOrganizationId();
  const matches = useMatches();
  const match = matches.at(-1);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // Extract key from title (remove array index if present)
  const key = title.replace(/\[\d+\]$/, '');
  const showFilterLink = organizationId && path === 'metadata';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        title={`Key: ${title}`}
        showClose={true}
        className="max-h-[80vh] max-w-4xl"
        childrenClassName="overflow-auto"
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              variant="outlined"
              color="secondary"
              size="sm"
              onClick={handleCopy}
            >
              Copy
            </Button>
            {showFilterLink && (
              <Tooltip title="Show similar runs with this metadata key and value">
                <Link
                  to={{
                    pathname: `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${
                      match?.params.sessionId ?? run?.session_id
                    }`,
                    search: `?searchModel=${encodeURIComponent(
                      JSON.stringify({
                        filter: `and(eq(metadata_key, '${key}'), eq(metadata_value, ${JSON.stringify(
                          content
                        )}))`,
                      })
                    )}`,
                  }}
                >
                  <Button variant="outlined" color="secondary" size="sm">
                    Apply Filter
                  </Button>
                </Link>
              </Tooltip>
            )}
          </div>
        </div>
        <pre className="whitespace-pre-wrap break-words rounded border bg-secondary p-4 font-mono text-sm">
          {content}
        </pre>
      </DialogContent>
    </Dialog>
  );
}

export interface CompleteMetadataCardProps {
  run: RunSchema;
}

interface JsonNestedListProps {
  data: { [key: string]: unknown };
  isNestedList?: boolean;
  path?: string;
  run?: RunSchema;
  depth?: number;
}

interface JsonValueDisplayProps {
  label: string;
  value: unknown;
  title: string;
  path?: string;
  run?: RunSchema;
  indentPx?: number;
}

function JsonValueDisplay({
  label,
  value,
  title,
  path,
  run,
}: JsonValueDisplayProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const stringValue =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const { textRef, isTextClamped } =
    useTextClamped<HTMLSpanElement>(stringValue);

  return (
    <div className="py-0.5">
      <div className="flex min-w-0 items-center gap-2">
        <Text variant="sm" className="flex-shrink-0">
          {label}:
        </Text>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex min-w-0 items-center">
            <Text
              ref={textRef}
              variant="sm"
              className="min-w-0 truncate text-quaternary hover:text-tertiary"
            >
              {isUrl(value) ? (
                <a
                  href={value}
                  target="_blank"
                  rel="noreferrer"
                  className="block truncate text-blue-500 underline hover:text-blue-400"
                >
                  {stringValue}
                </a>
              ) : (
                stringValue
              )}
            </Text>
          </div>
          {isTextClamped && (
            <Button
              variant="plain"
              size="sm"
              onClick={() => setModalOpen(true)}
              className="flex-shrink-0 text-xs opacity-70 hover:opacity-100"
            >
              show all
            </Button>
          )}
        </div>
      </div>
      <FullContentDialog
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={title}
        content={stringValue}
        path={path}
        run={run}
      />
    </div>
  );
}

interface ArrayItemProps {
  item: unknown;
  index: number;
  arrayKey: string;
  indentPx: number;
  path?: string;
  run?: RunSchema;
  depth?: number;
}

function ArrayItem({
  item,
  index,
  arrayKey,
  indentPx,
  path,
  run,
  depth = 0,
}: ArrayItemProps) {
  const itemKey = `${arrayKey}[${index}]`;
  const newPath = [path, `${arrayKey}[${index}]`].filter(Boolean).join('.');

  if (Array.isArray(item)) {
    if (item.length === 0) {
      return (
        <div className="py-0.5">
          <Text className="whitespace-pre-wrap">
            <RunsAwareJsonLeaf
              value={[index.toString(), []]}
              path={newPath}
              run={run}
            />
          </Text>
        </div>
      );
    }
    return (
      <div className="py-0.5">
        <JsonInlineCollapsibleRow label={index.toString()} depth={depth}>
          {item.map((child, childIdx) => (
            <ArrayItem
              key={childIdx}
              item={child}
              index={childIdx}
              arrayKey={itemKey}
              indentPx={indentPx}
              path={newPath}
              run={run}
              depth={depth + 1}
            />
          ))}
        </JsonInlineCollapsibleRow>
      </div>
    );
  }

  if (typeof item === 'object' && item !== null) {
    if (Object.keys(item as Record<string, unknown>).length === 0) {
      return (
        <div className="py-0.5">
          {`${index}: `} <Text variant="sm">{'{}'}</Text>
        </div>
      );
    }
    return (
      <div className="py-0.5">
        {depth > 0 ? (
          <JsonInlineCollapsibleRow label={index.toString()} depth={depth}>
            <JsonNestedList
              data={item as { [key: string]: unknown }}
              path={newPath}
              isNestedList
              run={run}
              depth={depth + 1}
            />
          </JsonInlineCollapsibleRow>
        ) : (
          <JsonCollapsibleListItem
            trigger={index.toString()}
            data={item}
            depth={depth}
          >
            <JsonNestedList
              data={item as { [key: string]: unknown }}
              path={newPath}
              isNestedList
              run={run}
              depth={depth + 1}
            />
          </JsonCollapsibleListItem>
        )}
      </div>
    );
  }

  return (
    <JsonValueDisplay
      label={index.toString()}
      value={item}
      title={`${arrayKey}[${index}]`}
      path={newPath}
      run={run}
      indentPx={indentPx}
    />
  );
}

function RunsAwareJsonLeaf(props: {
  value: [string, unknown];
  path: string;
  run?: RunSchema;
}) {
  const organizationId = useOrganizationId();
  const matches = useMatches();
  const match = matches.at(-1);
  const [modalOpen, setModalOpen] = useState(false);

  const [key, value] = props.value;
  const stringValue =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const { textRef, isTextClamped } = useTextClamped<HTMLElement>(stringValue);

  if (organizationId && props.path === 'metadata') {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <Tooltip title="Show runs with this metadata key">
          <Link
            to={{
              pathname: `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${
                match?.params.sessionId ?? props.run?.session_id
              }`,
              search: `?searchModel=${encodeURIComponent(
                JSON.stringify({
                  filter: `eq(metadata_key, '${key}')`,
                })
              )}`,
            }}
            className="flex-shrink-0 font-medium underline"
          >
            {key}
          </Link>
        </Tooltip>
        <span className="flex-shrink-0">:</span>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex min-w-0 items-center">
            <Tooltip
              title={
                isUrl(value)
                  ? 'Open external link'
                  : 'Show runs with this metadata key and value'
              }
            >
              {isUrl(value) ? (
                <a
                  ref={textRef as React.RefObject<HTMLAnchorElement>}
                  href={value}
                  target="_blank"
                  rel="noreferrer"
                  className="block min-w-0 truncate text-blue-500 underline hover:text-blue-400"
                >
                  {stringValue}
                </a>
              ) : (
                <Link
                  to={{
                    pathname: `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${
                      match?.params.sessionId ?? props.run?.session_id
                    }`,
                    search: `?searchModel=${encodeURIComponent(
                      JSON.stringify({
                        filter: `and(eq(metadata_key, '${key}'), eq(metadata_value, ${JSON.stringify(
                          value
                        )}))`,
                      })
                    )}`,
                  }}
                  className="min-w-0"
                >
                  <Text
                    ref={textRef}
                    variant="sm"
                    className="block truncate text-quaternary underline hover:text-tertiary"
                  >
                    {stringValue}
                  </Text>
                </Link>
              )}
            </Tooltip>
          </div>
          {isTextClamped && (
            <Button
              variant="plain"
              size="sm"
              onClick={() => setModalOpen(true)}
              className="flex-shrink-0 text-xs opacity-70 hover:opacity-100"
            >
              show all
            </Button>
          )}
        </div>
        <FullContentDialog
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={key}
          content={stringValue}
          path={props.path}
          run={props.run}
        />
      </div>
    );
  }

  return (
    <JsonValueDisplay
      label={key}
      value={value}
      title={key}
      path={props.path}
      run={props.run}
    />
  );
}

function JsonCollapsibleListItem(props: {
  trigger: string;
  children: ReactNode;
  data: unknown;
  depth?: number;
}) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <div className="py-0. flex gap-1">
        <Button
          variant="plain"
          color="secondary"
          size="sm"
          className="flex-none"
          onClick={() => setOpen((o) => !o)}
        >
          <Text className={`${props.depth === 0 ? 'uppercase' : ''}`}>
            {props.trigger}
          </Text>
          {open ? (
            <ChevronDownIcon className="size-4" />
          ) : (
            <ChevronRightIcon className="size-4" />
          )}
        </Button>
        <CopyInlineLink
          className="ml-1"
          value={JSON.stringify(props.data)}
          showValuePreview={false}
          prefix={props.trigger}
        />
      </div>

      {open && props.children}
    </>
  );
}

function JsonInlineCollapsibleRow(props: {
  label: string;
  children: ReactNode;
  run?: RunSchema;
  depth: number;
}) {
  const organizationId = useOrganizationId();
  const matches = useMatches();
  const match = matches.at(-1);
  const [open, setOpen] = useState(true);
  return (
    <>
      <div className="flex min-w-0 items-center gap-2">
        {/* only have link for depth 1 or top level metadata keys */}
        {props.depth <= 1 ? (
          <Tooltip title="Show runs with this metadata key">
            <Link
              to={{
                pathname: `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${
                  match?.params.sessionId ?? props.run?.session_id
                }`,
                search: `?searchModel=${encodeURIComponent(
                  JSON.stringify({
                    filter: `eq(metadata_key, '${props.label}')`,
                  })
                )}`,
              }}
              className="flex-shrink-0 text-sm font-medium underline"
            >
              {props.label}
            </Link>
          </Tooltip>
        ) : (
          <Text className="flex-shrink-0 text-sm font-medium">
            {props.label}
          </Text>
        )}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <IconButton
            icon={ChevronRightIcon}
            label="Toggle details"
            variant="plain"
            color="secondary"
            size="sm"
            onClick={() => setOpen((o) => !o)}
            iconClassName={cn('transition-transform', open ? 'rotate-90' : '')}
          />
        </div>
      </div>

      {open && props.children}
    </>
  );
}

function JsonNestedList({
  data,
  path = '',
  isNestedList = false,
  run,
  depth = 0,
}: JsonNestedListProps) {
  const indentPx = 24;

  const renderItem = (key: string, value: unknown, index: number) => {
    const newPath = [path, key].filter(Boolean).join('.');

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <div key={key} className="py-0.5">
            <Text className="whitespace-pre-wrap">
              <RunsAwareJsonLeaf value={[key, []]} path={path} run={run} />
            </Text>
          </div>
        );
      } else {
        return (
          <React.Fragment key={key}>
            {index > 0 && depth === 0 && (
              <div className="my-2 border-t border-secondary" />
            )}
            <div className="py-0.5">
              {depth > 0 ? (
                <JsonInlineCollapsibleRow label={key} run={run} depth={depth}>
                  <div style={{ paddingLeft: `${indentPx}px` }}>
                    {value.map((item, index) => (
                      <ArrayItem
                        key={index}
                        item={item}
                        index={index}
                        arrayKey={key}
                        indentPx={indentPx}
                        path={newPath}
                        run={run}
                        depth={depth + 1}
                      />
                    ))}
                  </div>
                </JsonInlineCollapsibleRow>
              ) : (
                <JsonCollapsibleListItem
                  trigger={key}
                  data={value}
                  depth={depth}
                >
                  <div style={{ paddingLeft: `${indentPx}px` }}>
                    {value.map((item, index) => (
                      <ArrayItem
                        key={index}
                        item={item}
                        index={index}
                        arrayKey={key}
                        indentPx={indentPx}
                        path={newPath}
                        run={run}
                        depth={depth + 1}
                      />
                    ))}
                  </div>
                </JsonCollapsibleListItem>
              )}
            </div>
          </React.Fragment>
        );
      }
    } else if (typeof value === 'object' && value !== null) {
      if (Object.keys(value).length === 0) {
        return (
          <div key={key} className="py-0.5">
            {`${key}: `} <Text variant="sm">{'{}'}</Text>
          </div>
        );
      } else {
        return (
          <React.Fragment key={key}>
            {index > 0 && depth === 0 && (
              <div className="my-2 border-t border-secondary" />
            )}
            <div className="py-0.5">
              {depth > 0 ? (
                <JsonInlineCollapsibleRow label={key} depth={depth}>
                  <JsonNestedList
                    data={value as { [key: string]: unknown }}
                    path={newPath}
                    isNestedList
                    run={run}
                    depth={depth + 1}
                  />
                </JsonInlineCollapsibleRow>
              ) : (
                <JsonCollapsibleListItem
                  trigger={key}
                  data={value}
                  depth={depth}
                >
                  <JsonNestedList
                    data={value as { [key: string]: unknown }}
                    path={newPath}
                    isNestedList
                    run={run}
                    depth={depth + 1}
                  />
                </JsonCollapsibleListItem>
              )}
            </div>
          </React.Fragment>
        );
      }
    } else {
      return (
        <div key={key} className="py-0.5">
          <Text className="whitespace-pre-wrap">
            <RunsAwareJsonLeaf value={[key, value]} path={path} run={run} />
          </Text>
        </div>
      );
    }
  };

  return (
    <div
      className="flex-1"
      style={{ paddingLeft: isNestedList ? `${indentPx}px` : '0px' }}
    >
      <div className="space-y-0.5">
        {Object.entries(data)
          .sort((a, b) => {
            /**
             * Sort Order:
             * - primitives first
             * - then arrays
             *   - inside by index
             * - then objects
             *   - inside by key
             */
            const isAPrimitive = typeof a[1] !== 'object' || a[1] === null;
            const isBPrimitive = typeof b[1] !== 'object' || b[1] === null;
            if (isAPrimitive && !isBPrimitive) {
              return -1;
            } else if (!isAPrimitive && isBPrimitive) {
              return 1;
            } else if (Array.isArray(a[1]) && !Array.isArray(b[1])) {
              return -1;
            } else if (!Array.isArray(a[1]) && Array.isArray(b[1])) {
              return 1;
            } else {
              return a[0].localeCompare(b[0]);
            }
          })
          .map(([key, value], index) => renderItem(key, value, index))}
      </div>
    </div>
  );
}

const RunExtraCard = ({ run }: CompleteMetadataCardProps) => {
  return (
    <div className="flex-1 flex-col items-stretch overflow-hidden rounded-md border border-secondary p-2">
      {run.extra && <JsonNestedList data={run.extra} run={run} />}
    </div>
  );
};

export { RunExtraCard };
